import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

/* -------- packages -------- */
import moment from 'moment';
import { deleteNotificationRequest, fetchNotificationsRequest, updateNotificationRequest } from '../../../store/actions';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { UncontrolledTooltip } from 'reactstrap';
/* -------- ------- -------- */

const Notification = ({ notification, activeTab, isLastNotification }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isDeleted, setIsDeleted] = useState(false);
  const [deletedNotificationData, setDeletedNotificationData] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const [timerDelay, setTimerDelay] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (timerDelay > 0) {
      setSecondsLeft(Math.floor(timerDelay / 1000));
      
      const interval = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 0) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [timerDelay]);

  let isRead = notification?.isRead;

  // Classes CSS conditionnelles pour le style
  const getNotificationClasses = () => {
    let classes = `text-reset notification-item`;
    
    if (activeTab == 1 || activeTab == 2) {
      if (notification?.isArchived) {
        classes += ' isArchived';
      } else if (isRead) {
        classes += ' isRead';
      } else {
        classes += ' isNotRead';
      }
    }
    
    return classes;
  };

  useEffect(() => {
    // Cleanup timer on unmount
    return () => {
      if (undoTimer) {
        clearTimeout(undoTimer);
      }
    };
  }, [undoTimer]);

  const handleReadNotification = (read) => { // mark as read notification function
    const notif = {
      id: notification?.id,
      isRead: read,
      rowVersion: notification?.rowVersion,
    }

    dispatch(updateNotificationRequest(notif));
  };

  const handleArchiveNotification = () => { // archive notification function
    const notif = {
      id: notification?.id,
      isArchived: !notification?.isArchived,
      rowVersion: notification?.rowVersion,
    };

    dispatch(updateNotificationRequest(notif));
  }

  const handleDeleteNotification = () => { // delete notification function
    // Empêcher la suppression de la dernière notification
    if (isLastNotification) {
      return;
    }

    // Sauvegarder l'état de la notification avant suppression
    setDeletedNotificationData({
      ...notification,
      originalIsRead: notification.isRead,
      originalIsArchived: notification.isArchived
    });
    
    setIsDeleted(true);
    
    const timer = setTimeout(() => {
      dispatch(deleteNotificationRequest(notification?.id));
      setIsDeleted(false);
      setDeletedNotificationData(null);
    }, 10000);
    
    setUndoTimer(timer);
    setTimerDelay(10000);
  };


  const handleUndoDelete = () => {
    if (deletedNotificationData && undoTimer) {
      clearTimeout(undoTimer);
      setUndoTimer(null);
      
      setIsDeleted(false);
      setTimerDelay(0);
      setDeletedNotificationData(null);
    }
  };

  if (isDeleted) {
    return (
      <div className={`${getNotificationClasses()} notification-undo-state`}>
        <div className={`d-flex align-items-center`}>
          <div className="avatar-xs flex-shrink-0 me-3 d-flex align-items-center justify-content-center" style={{
            backgroundColor: '#997404 !important',
            color: '#ffeaa7 !important'
          }}>
              {(notification?.cat == "USER_BANNED") && <i className="mdi mdi-account-remove-outline"/>}
              {(notification?.cat == "USER_NEW") && <i className="mdi mdi-account-plus-outline"/>}
              {(notification?.cat == "USER") && <i className='bx bx-user-circle' ></i>}

              {(notification?.cat == "QUOTE_NEW") && <i className="mdi mdi-book-plus-outline"/>}
              {(notification?.cat == "QUOTE_EXPIRED") && <i className="mdi mdi-book-alert-outline"/>}

              {(notification?.cat == "SYSTEM") && <i className="mdi mdi-application-cog" style={{}} />}
              {(notification?.cat == "INFO") && <i className='bx bxs-info-circle'></i>}
          </div>
          
          {/* Structure pour aligner le badge avec le contenu et le bouton à droite */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            width: '100%' 
          }}>
            {/* Première ligne : Badge et bouton */}
            
            {/* Deuxième ligne : Contenu avec indentation qui peut aller jusqu'au bord */}
            <div style={{ marginLeft: '15px', width: 'calc(100% - 15px)' }}>
              <h6 className="mt-0 mb-1">
                {t(notification?.cat + "_NOTIFICATION")}
              </h6>
              <div className="font-size-12 text-muted">
                <p className="mb-1">
                  {notification?.lib}
                </p>
                {notification.route && (
                  <p className="mb-1 notifUrl" onClick={ () => {navigate(notification.route);}}>
                    {t('notifUrl')}
                  </p>
                )}
              </div>
            </div>

            <div className='spacer w-100 mt-2 mb-2'></div>
            <div className="deleteFooter d-flex align-items-center justify-content-between p-0" style={{ width: '100%' }}>
              <div className="notification-deleted-badge mt-0">
                <small className="deleted-text w-100">
                  {t('notificationWillBeDeleted') + " " + secondsLeft + " " + t('seconds')}
                </small>
              </div>
              
              <div className="notificationUndoBtns" style={{ flexShrink: 0, marginTop: '0' }}>
                <button 
                  className="btn btn-sm btn-outline-primary undo-button"
                  onClick={handleUndoDelete}
                  title={t('undo')}
                  style={{
                    height: '28px',
                    lineHeight: '1',
                    padding: '4px 8px',
                    fontSize: '0.75em',
                    display: 'flex',
                    alignItems: 'center',
                    verticalAlign: 'top',
                    marginTop: '0',
                    marginBottom: '0'
                  }}
                >
                  <i className="mdi mdi-undo-variant" style={{ marginRight: '4px', fontSize: '1.1em' }}></i>
                  {t('undo')}
                </button>
              </div>
            </div>
            

          </div>
        </div>
      </div>
    );
  }

  return (
    <div onClick={() => {}} className={getNotificationClasses()}>
      <div className={`d-flex align-items-center`}>
        <div className="avatar-xs flex-shrink-0 me-3 d-flex align-items-center justify-content-center">
            {(notification?.cat == "USER_BANNED") && <i className="mdi mdi-account-remove-outline"/>}
            {(notification?.cat == "USER_NEW") && <i className="mdi mdi-account-plus-outline"/>}
            {(notification?.cat == "USER") && <i className='bx bx-user-circle' ></i>}

            {(notification?.cat == "QUOTE_NEW") && <i className="mdi mdi-book-plus-outline"/>}
            {(notification?.cat == "QUOTE_EXPIRED") && <i className="mdi mdi-book-alert-outline"/>}

            {(notification?.cat == "SYSTEM") && <i className="mdi mdi-application-cog" style={{}} />}
            {(notification?.cat == "INFO") && <i className='bx bxs-info-circle'></i>}
        </div>
        <div className="flex-grow-1">
            {t(notification?.cat + "_NOTIFICATION")}
          <h6 className="mt-0 mb-1">
          </h6>
          <div className="font-size-12 text-muted">
            <p className="mb-1">
              {notification?.lib}
            </p>
            {notification.route && (
              <p className="mb-1 notifUrl" onClick={ () => {navigate(notification.route);}}>
                {t('notifUrl')}
              </p>
            )}
          </div>
        </div>
        
        {/* Boutons d'action alignés à droite */}
        <div className="notificationActionBtns flex-shrink-0 ms-3 d-flex flex-column">
          {(!notification?.isRead) &&
          <>
            <span id={"notificationActionBtnRead_" + notification.id} className="mdi mdi-check-bold btnIconHover" onClick={() => {handleReadNotification(!isRead)}}></span>
            <UncontrolledTooltip
                transition={{ timeout: 300 }}
                placement="top"
                fade={false}
                target={"notificationActionBtnRead_" + notification.id}
                >
                {t("markAsRead")}
            </UncontrolledTooltip>
          </>
          }
          <span id={"notificationActionBtnArchive_" + notification.id} className="mdi mdi-archive-outline btnIconHover" onClick={() => {handleArchiveNotification()}}></span>
          <UncontrolledTooltip
              transition={{ timeout: 300 }}
              placement="top"
              fade={false}
              target={"notificationActionBtnArchive_" + notification.id}
              >
              {t( notification.isArchived ? "unarchive" : "archive")}
          </UncontrolledTooltip>
          
          {/* Bouton de suppression conditionnel */}
          <span 
            id={"notificationActionBtnDelete_" + notification.id}  
            className={`mdi mdi-trash-can-outline btnIconHover ${isLastNotification ? 'disabled' : ''}`}
            onClick={() => {!isLastNotification && handleDeleteNotification()}}
            style={{ 
              opacity: isLastNotification ? 0.3 : 1, 
              cursor: isLastNotification ? 'not-allowed' : 'pointer' 
            }}
          ></span>
          <UncontrolledTooltip
              transition={{ timeout: 300 }}
              placement="top"
              fade={false}
              target={"notificationActionBtnDelete_" + notification.id}
              >
              {isLastNotification ? t("cannotDeleteLastNotification") : t("delete")}
          </UncontrolledTooltip>
        </div>
      </div>
    </div>
  );
};

export default Notification;